import {
  IlogisticServiceTrelloCardData,
  IlogisticServiceDetails,
  LOCATION_PLACE_TYPE,
  IAddExternalProductsToLogisticService,
  IAddExternalProductReference,
  IProductReference,
  DTOupdateLogisticServiceStatus,
  LOGISTIC_SERVICE_STATUS_NAME,
  IlogisticServiceDetailsToolsData,
  TlogisticServiceStatus,
  IEmployeeAvailableTool
} from '../types/services';
import { PRODUCT_CONDITION } from '../types/products';

/**
 * Determina si un servicio logístico es un offboarding con destino a bodega
 * @param logisticServiceData - Datos del servicio logístico
 * @returns true si es offboarding con destino a bodega
 */
export const checkDestinationIsWarehouse = (logisticServiceData?: IlogisticServiceTrelloCardData): boolean => {
  if (!logisticServiceData) return false;
  return logisticServiceData.destination?.place === LOCATION_PLACE_TYPE.NUDOS;
};

interface IShowUpdateToolsTrigger {
  originPlace?: LOCATION_PLACE_TYPE | null;
  destinationPlace?: LOCATION_PLACE_TYPE | null;
  statusName?: TlogisticServiceStatus | null;
}
/**
 * Indica si debe mostrarse el control para actualizar herramientas en offboarding hacia bodega.
 * Solo rutas usuario→bodega u oficina→bodega y servicio no cancelado.
 */
export const showUpdateToolsTrigger = ({
  originPlace,
  destinationPlace,
  statusName
}: IShowUpdateToolsTrigger): boolean => {
  if (statusName === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED) return false;

  const isOriginUser = originPlace === LOCATION_PLACE_TYPE.USER;
  const isOriginOffice = originPlace === LOCATION_PLACE_TYPE.OFFICE;
  const isDestinationWarehouse = destinationPlace === LOCATION_PLACE_TYPE.NUDOS;

  if (isOriginUser && isDestinationWarehouse) return true;
  if (isOriginOffice && isDestinationWarehouse) return true;

  return false;
};

export const isEmployeeAvailableToolForOffboardingOriginList = (item: IEmployeeAvailableTool): boolean => {
  if (!item?.isAvailableTool) return false;
  const raw = item.logisticStatus;
  const rawSecondary = item.shipmentStatusName;
  const rawTertiary = item.referenceModel;
  const validStatusesInCourse = ['entregado', 'cancelado'];
  const validStatusesCompleted = ['entregado'];
  const validReferenceModels = ['ExternalProduct'];
  const ls = raw?.toLowerCase();
  const lsSecondary = rawSecondary?.toLowerCase();
  return (!!ls && validStatusesInCourse?.includes(ls)) || (!!lsSecondary && validStatusesCompleted?.includes(lsSecondary)) || (!!rawTertiary && validReferenceModels?.includes(rawTertiary));
};

/**
 * Construye el DTO para agregar productos externos a un servicio logístico
 * @param serviceDetails - Detalles del servicio logístico
 * @param selectedTools - Lista de herramientas seleccionadas
 * @param toolsSerials - Mapa de índices a seriales de las herramientas
 * @returns DTO para agregar productos externos o null si faltan datos requeridos
 */
export const buildAddExternalProductsDTO = (
  serviceDetails: IlogisticServiceDetails,
  selectedTools: IProductReference[],
  toolsSerials: { [key: number]: string }
): IAddExternalProductsToLogisticService | null => {
  const organizationId = serviceDetails?.organization?.organizationId;
  const logisticServiceId = serviceDetails?.id;

  if (!organizationId || !logisticServiceId) {
    return null;
  }

  const user = serviceDetails.collectionInfo?.user;
  const countryCode =
    serviceDetails.originLocation?.country?.code ||
    serviceDetails.collectionInfo?.delivererCountryPhone?.code ||
    serviceDetails.organization?.country?.code;

  if (!countryCode) {
    return null;
  }

  const originPlace = serviceDetails.originLocation?.place;
  const place = originPlace || null;

  if (!place) {
    return null;
  }

  const optionId = place === LOCATION_PLACE_TYPE.USER ? 3 : 1;

  const assigneeInfo =
    user?.userId && user?.firstName && user?.lastName
      ? {
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName
      }
      : undefined;

  const references: IAddExternalProductReference[] = selectedTools?.map((tool, index) => {
    const serial = toolsSerials[index];
    if (!serial) {
      throw new Error(`El serial es requerido para la herramienta ${tool.name}`);
    }

    const individualToolData = {
      serial,
      productCondition: PRODUCT_CONDITION.USED_GOOD as string,
      warranty: '',
      optionId,
      locationInfo: {
        place,
        placeName: null
      },
      countryCode,
      ...(assigneeInfo && { userInfo: assigneeInfo })
    };

    return {
      id: tool.id,
      sku: tool.sku,
      name: tool.name,
      category: tool.category || '',
      quantity: 1,
      individualToolsData: [individualToolData]
    };
  });

  return {
    organizationId,
    logisticServiceId,
    references
  };
};

interface DeliveredTool {
  tool: IlogisticServiceDetailsToolsData;
  originalIndex: number;
}

/**
 * Extrae los IDs de productos de las herramientas no entregadas
 * @param unselectedTools - Array de herramientas no seleccionadas
 * @returns Array de IDs de productos no entregados
 */
export const extractUndeliveredProductIds = (unselectedTools: DeliveredTool[]): number[] => {
  return unselectedTools
    .map(({ tool }) => tool?.productId)
    .filter((productId): productId is number => productId !== null && productId !== undefined);
};

/**
 * Construye el payload para marcar un servicio logístico como entregado
 * @param userId - ID del usuario que realiza la acción
 * @param deliveryDate - Fecha de entrega
 * @param undeliveredProductIds - IDs de productos no entregados (opcional)
 * @returns Payload para actualizar el estado del servicio logístico
 */
export const buildMarkAsDeliveredPayload = (
  userId: number,
  deliveryDate: string,
  undeliveredProductIds?: number[]
): DTOupdateLogisticServiceStatus => {
  return {
    userId,
    status: LOGISTIC_SERVICE_STATUS_NAME.DELIVERED,
    delivered: {
      deliveryDate,
      ...(undeliveredProductIds &&
        undeliveredProductIds?.length > 0 && { notDeliveredProductIds: undeliveredProductIds })
    }
  };
};
